import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { RegistrationDetails } from "@/lib/api/registration-approval";
import type { Branch } from "@/lib/api/branches";
import type { PatronType } from "@/lib/api/patron-types";
import { CheckCircle2, Loader2, RotateCcw } from "lucide-react";

interface RejectedRegistrationReviewProps {
  open: boolean;
  details: RegistrationDetails | null;
  patronTypes: PatronType[];
  branches: Branch[];
  busyAction: "reopen" | "approve" | null;
  onOpenChange: (open: boolean) => void;
  onReopen: () => void;
  onApprove: (input: { patronTypeId?: string; homeBranchId?: string; notes?: string }) => void;
}

export function RejectedRegistrationReview({
  open,
  details,
  patronTypes,
  branches,
  busyAction,
  onOpenChange,
  onReopen,
  onApprove,
}: RejectedRegistrationReviewProps) {
  const [patronTypeId, setPatronTypeId] = useState("");
  const [homeBranchId, setHomeBranchId] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setPatronTypeId(details?.patronType?.id || details?.metadata?.patronTypeId || "");
    setHomeBranchId(details?.homeBranch?.id || "");
    setNotes("");
  }, [details]);

  const rejectionNotes = details?.metadata?.rejectionNotes as string | undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle>Rejected application</DialogTitle>
          <DialogDescription>
            Send this account back to the queue, or approve it after correcting the patron type and
            branch.
          </DialogDescription>
        </DialogHeader>
        {details && (
          <div className="space-y-4">
            {rejectionNotes ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Last rejection: {rejectionNotes}
              </p>
            ) : null}
            <div className="space-y-1.5">
              <Label>Patron type</Label>
              <Select value={patronTypeId || undefined} onValueChange={setPatronTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a patron type" />
                </SelectTrigger>
                <SelectContent>
                  {patronTypes
                    .filter((type) => type.isActive)
                    .map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {branches.length > 0 && (
              <div className="space-y-1.5">
                <Label>Home branch</Label>
                <Select value={homeBranchId || undefined} onValueChange={setHomeBranchId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Keep current branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                placeholder="Optional note for this decision"
              />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onReopen} disabled={busyAction !== null || !details}>
            {busyAction === "reopen" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="mr-2 h-4 w-4" />
            )}
            Reopen
          </Button>
          <Button
            onClick={() =>
              onApprove({
                patronTypeId: patronTypeId || undefined,
                homeBranchId: homeBranchId || undefined,
                notes: notes || undefined,
              })
            }
            disabled={busyAction !== null || !details}
          >
            {busyAction === "approve" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            )}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
